// src/features/commodities-info/components/CommodityCotChart.tsx
//
// Component Island: 52-week COT net-positioning trend (Commercials vs
// Speculators) for a single commodity. Fetches independently of the parent
// page by reusing the existing commodities-ranking COT endpoint
// (GET /api/v1/commodities-ranking/cot/{symbol}?days=365) — the same data
// source as the ranking page's CotPopup, rendered here as an inline card
// island rather than a modal.
//
// Commercials (hedgers) use sky (#0ea5e9); Speculators (specs) use emerald
// (#10b981), consistent with CotPopup. Card chrome + slate hexes match the
// institutional palette. Gracefully renders an empty state for the ~half of
// commodities with no CFTC COT coverage (the endpoint returns an empty series,
// not a 404).

import { useEffect, useState } from 'react';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { fetchCotHistory, type CotHistoryResponse } from '../../commodities-ranking/api';

// COT contract counts are large integers; render compactly (124.3k / 1.2M).
const formatContracts = (value: number | null): string => {
    if (value == null || !Number.isFinite(value)) return 'N/A';
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}k`;
    return `${sign}${abs.toFixed(0)}`;
};

const CotTooltip = ({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: { name: string; value: number; color: string }[];
    label?: string;
}) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
            <p className="font-bold text-slate-900 dark:text-white">{label}</p>
            {payload.map((p) => (
                <p key={p.name} style={{ color: p.color }}>
                    {p.name}:{' '}
                    <span className="font-mono">{formatContracts(p.value)}</span>
                </p>
            ))}
        </div>
    );
};

export const CommodityCotChart = ({ symbol }: { symbol: string }) => {
    const [data, setData] = useState<CotHistoryResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        setData(null);

        fetchCotHistory(symbol, 365)
            .then((res) => {
                if (!cancelled) setData(res);
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load COT history', err);
                setError('Positioning data unavailable.');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    const series = data?.series ?? [];
    const latest = series.length > 0 ? series[series.length - 1] : null;

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 flex flex-col h-[350px] shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    COT Net Positioning
                </h2>
                <span className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    52 Weeks
                    {data?.cot_symbol && (
                        <span className="ml-2 rounded-md bg-slate-100 dark:bg-slate-700 px-2 py-0.5 font-mono normal-case text-slate-500 dark:text-slate-300">
                            CFTC {data.cot_symbol}
                        </span>
                    )}
                </span>
            </div>

            <div className="relative flex-1 w-full">
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center text-amber-500 animate-pulse text-sm">
                        Loading positioning…
                    </div>
                )}
                {!isLoading && error && (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm">
                        {error}
                    </div>
                )}
                {!isLoading && !error && series.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-center px-4 text-slate-500 dark:text-slate-400 text-sm">
                        No CFTC positioning coverage for this commodity.
                    </div>
                )}

                {!isLoading && !error && series.length > 0 && (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={series}
                            margin={{ top: 10, right: 0, left: -20, bottom: 0 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#334155"
                                opacity={0.3}
                            />
                            <XAxis
                                dataKey="date"
                                tickFormatter={(val: string) =>
                                    val.substring(5, 10)
                                }
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                minTickGap={30}
                            />
                            <YAxis
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                width={48}
                                tickFormatter={formatContracts}
                            />
                            <Tooltip content={<CotTooltip />} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line
                                type="monotone"
                                name="Commercials"
                                dataKey="net_commercial"
                                stroke="#0ea5e9"
                                strokeWidth={2}
                                dot={false}
                            />
                            <Line
                                type="monotone"
                                name="Speculators"
                                dataKey="net_noncomm"
                                stroke="#10b981"
                                strokeWidth={2}
                                dot={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>

            {latest && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="rounded-lg border border-slate-100 dark:border-slate-700 px-3 py-2">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Net Commercials
                        </p>
                        <p className="font-mono font-semibold text-sky-600 dark:text-sky-400">
                            {formatContracts(latest.net_commercial)}
                        </p>
                    </div>
                    <div className="rounded-lg border border-slate-100 dark:border-slate-700 px-3 py-2">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Net Speculators
                        </p>
                        <p className="font-mono font-semibold text-green-600 dark:text-emerald-400">
                            {formatContracts(latest.net_noncomm)}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CommodityCotChart;
